// Application receipt handling; generated results.js may be replaced by deployment exports.
import { utils } from 'ethers'
import MANAGER_ABI from './abi/MerchantManager.json'
import SWAP_ABI from './abi/MerchantSwap.json'
import EXIT_ABI from './abi/cvcd-swap.json'
import MA_ABI from './abi/MA_V2.json'
import { MERCHANT_MANAGER, MERCHANT_SWAP, MA_V2, CVCD_SWAP, AMOUNT_DECIMALS } from './network'

const exit = new utils.Interface(EXIT_ABI)
const manager = new utils.Interface(MANAGER_ABI)
const swap = new utils.Interface(SWAP_ABI)
const ma = new utils.Interface(MA_ABI)
const erc20 = new utils.Interface([
    'error ERC20InsufficientBalance(address sender,uint256 balance,uint256 needed)',
    'error ERC20InsufficientAllowance(address spender,uint256 allowance,uint256 needed)'
])

const MESSAGES = {
    CVCD_EXIT_DISABLED: 'Early unlock is not yet enabled for this deployment.',
    MIGRATION_DEPLOYMENT_MISMATCH: 'Contract implementation differs from the frontend deployment configuration. Update the interface package.',
    OrderLocked: 'All payable orders must meet the original 7-day condition.',
    OrderClosed: 'This order is closed. Refresh the orders.',
    StairAlreadyConverted: 'Stair principal has already been migrated.',
    InvalidStairPrincipal: 'No active Stair principal is available to migrate.',
    ExitAlreadyClaimed: 'Exit refund has already been claimed.',
    NothingToRefund: 'No assets are available to refund.',
    CvcdSwapClosed: 'CVCD Swap is closed. Use early unlock to claim your refund.',
    InsufficientMigrationBalance: 'Refund inventory is insufficient. No assets were paid.',
    InsufficientWalletBalance: 'Refund inventory is insufficient. No assets were paid.',
    InvalidExitReceipt: 'Token delivery was incomplete. The refund was reverted.',
    InvalidCvcRefund: 'Token delivery was incomplete. The refund was reverted.',
    InvalidMigrationReceipt: 'Token delivery was incomplete. The migration was reverted.',
    IneligibleInitialIdentityClaim: 'Only Node users and above can claim free identities.',
    NoInitialIdentitiesAvailable: 'No first-time free identities remain. Use paid renewal.',

    MERCHANT_TOKEN_DECIMALS_MISMATCH: 'Token decimals do not match the merchant configuration.',
    MERCHANT_NETWORK_MISMATCH: 'Select the configured merchant network.',
    MERCHANT_ACCOUNT_CHANGED: 'Wallet changed. Please retry.',
    InsufficientTotalQuota: 'Withdrawal quota is insufficient. Reduce the amount or wait for recovery.',
    InsufficientFreeQuota: 'Free withdrawal quota is insufficient. Refresh the quote.',
    InsufficientInventory: 'The pool has insufficient inventory. Please try again later.',
    InsufficientFund: 'The pool has insufficient inventory. Please try again later.',
    NoReleasedUsd2: 'No released USD2 is available to claim.',
    InvalidAmount: 'Check the transaction amount.',
    InvalidFeeCvcAmount: 'Check the transaction amount.',
    UnsupportedMerchantAction: 'Unsupported merchant action.',
    CvcStakeLocked: 'This CVC stake has not matured yet.',
    FeeCvcLocked: 'This CVC stake has not matured yet.',
    CvcStakeWithdrawn: 'This CVC stake has already been withdrawn.',
    InvalidCvcStake: 'Invalid CVC stake order. Refresh the orders.',
    InvalidFeeCvcOrder: 'Invalid CVC stake order. Refresh the orders.',
    NoMaturedCvcStake: 'No matured CVC stake is available to claim.',
    NoMaturedFeeCvc: 'No matured CVC stake is available to claim.',
    SafeERC20FailedOperation: 'Token transfer failed. Check balance and allowance.',
    ERC20InsufficientBalance: 'Token balance is insufficient. Refresh your balances.',
    ERC20InsufficientAllowance: 'Token allowance is insufficient. Refresh and authorize again.'
}

// 只返回固定安全文案。支持常见嵌套 provider 错误，不传出原始异常或 RPC URL。
export function MerchantErrorMessage(error, fallback = 'Merchant transaction failed. Please retry.') {
    const nodes = [error, error?.error, error?.info?.error, error?.error?.error, error?.data, error?.error?.data]
    if (nodes.some(node => node?.code === 4001 || node?.code === 'ACTION_REJECTED')) return 'Transaction rejected.'
    for (const node of nodes) {
        if (MESSAGES[node?.code]) return MESSAGES[node.code]
        if (MESSAGES[node?.errorName]) return MESSAGES[node.errorName]
        const data = typeof node === 'string' ? node : node?.data
        if (typeof data !== 'string' || !/^0x[0-9a-f]+$/i.test(data)) continue
        for (const iface of [manager, swap, ma, exit, erc20]) {
            try {
                const parsed = iface.parseError(data)
                if (MESSAGES[parsed.name]) return MESSAGES[parsed.name]
            } catch { /* 不是此 ABI 的错误 */ }
        }
    }
    return fallback
}

const RESULT_EVENTS = new Set([
    'InitialIdentitiesClaimed', 'StairMigrated', 'StairRedeemedForMigration',
    'LegacyStairPrincipalSettled', 'LegacyStairProfitPaid', 'ExitRefundClaimed',
    'UsdPurchased', 'Usd2Redeemed', 'Usd2RedemptionReinvested', 'Usd2Withdrawn',
    'UsdDeposited', 'MerchantIdentityBound', 'CvcStaked', 'CvcUnstaked',
    'FeeCvcStaked', 'FeeCvcUnstaked', 'FeeCvcdBurned'
])

/**
 * 从成功回执提取当前用户的实际结果，绝不把报价当实际到账。
 * 返回 [{ source, name, logIndex, values }]；金额为代币单位字符串，
 * stakeId/orderId 为整数字符串，unlockTime/expiry/identity 为 number。
 */
export function ReadMerchantReceipt(receipt, account) {
    if (receipt?.status !== 1 || typeof account !== 'string') return []
    const events = []
    for (const log of receipt.logs || []) {
        const address = log.address?.toLowerCase()
        const source = address === MERCHANT_SWAP.toLowerCase() ? 'swap'
            : address === MERCHANT_MANAGER.toLowerCase() ? 'manager'
            : address === MA_V2.toLowerCase() ? 'ma'
            : address === CVCD_SWAP.toLowerCase() ? 'exit' : null
        if (!source) continue
        try {
            const parsed = ({ swap, manager, ma, exit }[source]).parseLog(log)
            if (!RESULT_EVENTS.has(parsed.name) || parsed.args.user?.toLowerCase() !== account.toLowerCase()) continue
            const values = {}
            parsed.eventFragment.inputs.forEach((input, index) => {
                const value = parsed.args[index]
                if (['stakeId', 'orderId'].includes(input.name)) values[input.name] = value.toString()
                else if (['unlockTime', 'expiry', 'identity'].includes(input.name)) values[input.name] = Number(value.toString())
                else if (input.type.startsWith('uint')) values[input.name] = utils.formatUnits(value, AMOUNT_DECIMALS)
                else values[input.name] = value
            })
            events.push({ source, name: parsed.name, logIndex: log.logIndex, values })
        } catch { /* 忽略其他事件及无法解析的日志，不伪造结果 */ }
    }
    return events
}
